import { pipe, of, merge, Subject, from, share as share$1, Subscription, combineLatest, isObservable, take, switchMap, startWith as startWith$1, shareReplay as shareReplay$1, BehaviorSubject, tap as tap$1, bufferWhen } from "rxjs";
import { map, filter, mergeMap, share, withLatestFrom, mergeWith, scan, startWith, shareReplay, tap } from "rxjs/operators";
import { tag } from "rxjs-spy/operators";
import { createElement, useState, useEffect } from "react";
import { dispatchAction as dispatchAction$1 } from "@ardoq/rxbeach";
const ofType = (...targetTypes) => {
  const types = new Set(targetTypes.map(({ type }) => type));
  return pipe(filter((action) => types.has(action.type)));
};
const extractPayload = () => map((action) => action.payload);
const withNamespace = (targetNamespace) => filter(
  ({ meta: { namespace } }) => namespace === void 0 || namespace === targetNamespace
);
const withoutNamespace = (excludedNamespace) => filter(
  ({ meta: { namespace } }) => excludedNamespace === void 0 ? namespace === void 0 : namespace !== excludedNamespace
);
const carry = (operator) => (observable) => {
  const hot = observable.pipe(share());
  return hot.pipe(
    operator,
    withLatestFrom(hot, (emitted, carried) => [carried, emitted])
  );
};
const apply = (operator) => mergeMap((payload) => of(payload).pipe(operator(payload)));
const spreadMap = (project) => map((args) => project(...args));
const coldMergeOperators = (...operators) => (source) => merge(...operators.map((operator) => source.pipe(operator)));
const mergeOperators = (...operators) => pipe(share(), coldMergeOperators(...operators));
const rethrowErrorGlobally = (error) => setTimeout(() => {
  throw error;
});
const defaultErrorSubject = new Subject();
defaultErrorSubject.subscribe((error) => rethrowErrorGlobally(error));
const wrapInArray = (val) => Array.isArray(val) ? val : [val];
const isActionReducer = (reducerFn) => "actions" in reducerFn.trigger;
const isStreamReducer = (reducerFn) => "source$" in reducerFn.trigger;
const connectActionTrigger = (trigger, reducerFn) => {
  const wrapper = (state, payload, namespace) => reducerFn(state, payload, namespace);
  wrapper.trigger = { actions: wrapInArray(trigger) };
  return wrapper;
};
const streamReducer = (trigger, reducerFn) => {
  const wrapper = (state, payload, namespace) => reducerFn(state, payload, namespace);
  wrapper.trigger = { source$: from(trigger) };
  return wrapper;
};
const actionReducer = (trigger, reducerFn) => {
  return connectActionTrigger(trigger, reducerFn);
};
const reducer = actionReducer;
const multiActionReducer = (trigger, reducerFn) => {
  return connectActionTrigger(trigger, reducerFn);
};
const ACTION_ORIGIN = Symbol("Action origin");
const combineReducers = (seed, reducers, { errorSubject = defaultErrorSubject, namespace } = {}) => {
  const actionReducers = reducers.filter(isActionReducer);
  const streamReducers = reducers.filter(isStreamReducer);
  const reducersByActionType = new Map(
    actionReducers.flatMap(
      (reducerFn) => reducerFn.trigger.actions.map((actionCreator2) => [
        actionCreator2.type,
        reducerFn
      ])
    )
  );
  const source$s = streamReducers.map(
    (reducerFn, i) => reducerFn.trigger.source$.pipe(
      map((payload) => ({ origin: i, value: payload }))
    )
  );
  return pipe(
    ofType(...actionReducers.flatMap((reducerFn) => reducerFn.trigger.actions)),
    map((action) => ({ origin: ACTION_ORIGIN, value: action })),
    mergeWith(...source$s),
    scan(
      ({ state }, packet) => {
        try {
          if (packet.origin === ACTION_ORIGIN) {
            const reducerFn2 = reducersByActionType.get(packet.value.type);
            return {
              caughtError: false,
              state: reducerFn2(state, packet.value.payload, namespace)
            };
          }
          const reducerFn = streamReducers[packet.origin];
          return {
            caughtError: false,
            state: reducerFn(state, packet.value, namespace)
          };
        } catch (e) {
          errorSubject.next(e);
          return {
            caughtError: true,
            state
          };
        }
      },
      { state: seed, caughtError: false }
    ),
    filter(({ caughtError }) => caughtError === false),
    map(({ state }) => state)
  );
};
const reduceState = (name, defaultState, reducers, errorSubject = defaultErrorSubject) => pipe(
  combineReducers(defaultState, reducers, {
    errorSubject
  }),
  startWith(defaultState),
  shareReplay({
    refCount: true,
    bufferSize: 1
  }),
  tag(name)
);
const _namespaceAction = (namespace, action) => Object.freeze({
  type: action.type,
  payload: action.payload,
  meta: Object.freeze({
    ...action.meta,
    namespace
  })
});
const namespaceActionCreator = (namespace, actionCreator2) => {
  const creator = (payload) => _namespaceAction(namespace, actionCreator2(payload));
  creator.type = actionCreator2.type;
  return Object.freeze(creator);
};
const namespaceActionDispatcher = (namespace, parentDispatcher) => (action) => parentDispatcher(_namespaceAction(namespace, action));
function createActionStream() {
  const actionSubject$ = new Subject();
  const action$2 = actionSubject$.pipe(tag("action$"), share$1());
  const dispatchAction2 = (action, namespace) => {
    if (namespace === void 0) {
      actionSubject$.next(action);
    } else {
      actionSubject$.next(_namespaceAction(namespace, action));
    }
  };
  return {
    /**
     * The main action stream for RxBeach
     */
    action$: action$2,
    /**
     * Dispatch an action to the action stream
     *
     * If namespace is provided, it will be set on the action.
     *
     * @param action The action to dispatch to action$
     * @param namespace Optional namespace to add to the action
     */
    dispatchAction: dispatchAction2
  };
}
const { action$, dispatchAction } = createActionStream();
const routine = (...args) => action$.pipe(...args);
const collectRoutines = (...routines) => ({
  subscribe: (observer) => {
    const subscriptions = [];
    const unsubscribe = () => subscriptions.forEach((sub) => sub.unsubscribe());
    const internalObserver = {
      next: (payload) => {
        var _a;
        return (_a = observer.next) == null ? void 0 : _a.call(observer, payload);
      },
      complete: () => {
        var _a;
        unsubscribe();
        (_a = observer.complete) == null ? void 0 : _a.call(observer);
      },
      error: (error) => {
        var _a;
        unsubscribe();
        (_a = observer.error) == null ? void 0 : _a.call(observer, error);
      }
    };
    subscriptions.push(
      ...routines.map((routine2) => routine2.subscribe(internalObserver))
    );
    return { unsubscribe };
  }
});
const subscribeRoutine = (action$2, routineToSubscribe, errorSubject = defaultErrorSubject) => {
  const observer = {
    error(err) {
      errorSubject.next(err);
      unsubscribable = routineToSubscribe.subscribe(observer);
    }
  };
  let unsubscribable = routineToSubscribe.subscribe(observer);
  return new Subscription(() => unsubscribable.unsubscribe());
};
const ensureArray = (a) => Array.isArray(a) ? a : [a];
const tapRoutine = (actionCreator2, body) => routine(
  ofType(
    ...ensureArray(
      actionCreator2
    )
  ),
  extractPayload(),
  tap(body)
);
const NOT_YET_EMITTED = Symbol("Returned from rxbeach/react:useStream");
const useStream = (source$, defaultValue) => {
  const [value, setValue] = useState(
    defaultValue !== void 0 ? defaultValue : NOT_YET_EMITTED
  );
  const [error, setError] = useState(null);
  if (error) {
    throw error;
  }
  useEffect(() => {
    const subscription = from(source$).subscribe({
      next: setValue,
      error: setError
    });
    return () => subscription.unsubscribe();
  }, [source$]);
  return value;
};
const useRoutine = (routine2, internalAction$ = action$, errorSubject = defaultErrorSubject) => {
  useEffect(() => {
    if (!routine2) return;
    const sub = subscribeRoutine(internalAction$, routine2, errorSubject);
    return () => sub.unsubscribe();
  }, [errorSubject, internalAction$, routine2]);
};
const connect = (Component, stream$, routine2) => Object.assign(
  (props) => {
    const value = useStream(stream$);
    useRoutine(routine2);
    if (value === NOT_YET_EMITTED) return null;
    const newProps = { ...props, ...value };
    return createElement(Component, newProps);
  },
  { displayName: `Connect(${Component.displayName || Component.name})` }
);
let instanceCount = 0;
const nextInstanceName = () => `view-${instanceCount++}`;
const connectInstance = (Component, createInstanceStream) => function ConnectInstance(props) {
  const [stream$] = useState(() => createInstanceStream(nextInstanceName()));
  const value = useStream(stream$);
  if (value === NOT_YET_EMITTED) return null;
  const newProps = { ...props, ...value };
  return createElement(Component, newProps);
};
const actionCreatorSymbol = Symbol("RXBEACH_ACTION_CREATOR");
const actionCreator = (type) => {
  const actionCreatorFn = (payload) => Object.freeze({
    type,
    payload,
    meta: Object.freeze({})
  });
  actionCreatorFn.type = type;
  actionCreatorFn.actionCreatorSymbol = actionCreatorSymbol;
  return Object.freeze(actionCreatorFn);
};
const namespacedActionCreator = (type) => {
  const actionCreatorFn = (namespace, payload) => Object.freeze({
    type,
    payload,
    meta: Object.freeze({
      namespace
    })
  });
  actionCreatorFn.type = type;
  actionCreatorFn.actionCreatorSymbol = actionCreatorSymbol;
  return Object.freeze(actionCreatorFn);
};
const isValidRxBeachAction = (action) => {
  if (!action || typeof action !== "object") {
    return false;
  }
  return "type" in action && "meta" in action && "payload" in action && typeof action.type === "string";
};
const isActionOfType = (creatorFn, action) => {
  if (!isValidRxBeachAction(action)) {
    return false;
  }
  return action.type === creatorFn.type;
};
const derivedStream = (name, ...dependencies) => combineLatest(dependencies).pipe(tag(name));
const reducedStream$1 = (initialState, reducers, {
  errorSubject,
  namespace,
  action$: action$$1 = action$
} = {}) => {
  const filteredAction$ = namespace === void 0 ? action$$1 : action$$1.pipe(withNamespace(namespace));
  const source$ = filteredAction$.pipe(
    combineReducers(initialState, reducers, {
      errorSubject,
      namespace
    })
  );
  return source$;
};
const reducedStream = (name, initialState, reducers, options = {}) => {
  const initialState$ = isObservable(initialState) ? initialState : of(initialState);
  return initialState$.pipe(
    take(1),
    switchMap(
      (initialState2) => reducedStream$1(initialState2, reducers, options).pipe(
        startWith$1(initialState2)
      )
    ),
    shareReplay$1({
      refCount: true,
      bufferSize: 1
    }),
    tag(name)
  );
};
class ObservableState {
  /**
   * Create a new ObservableState.
   *
   * Will not consume `source$` until {@link connect} has been called.
   *
   * @param name The name to store for the ObservableState, used by the stateStreamRegistry and for dev tools
   * @param source$ The observable this ObservableState will get it's values from
   * @param initialState The initial value of `.state` until `source$` has emitted a value
   */
  constructor(name, source$, initialState) {
    this.connect = () => {
      if (this.destroyed) {
        throw new Error("ObservableState has been destroyed");
      }
      this.subscription = this.source$.subscribe(this.subject);
    };
    this.unsubscribe = () => {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
      this.subject.unsubscribe();
    };
    this.source$ = from(source$);
    this.name = name;
    this.subject = new BehaviorSubject(initialState);
    this.subscribe = this.subject.subscribe.bind(this.subject);
    this.pipe = this.subject.pipe.bind(this.subject);
  }
  /**
   * @deprecated Only for use by RxJS
   */
  [Symbol.observable]() {
    return {
      subscribe: this.subscribe
    };
  }
  get state() {
    return this.subject.getValue();
  }
  get destroyed() {
    return this.subject.closed;
  }
}
class StateStreamRegistry {
  constructor() {
    this.streams = /* @__PURE__ */ new Map();
    this.started = false;
  }
  /**
   * Register a stream to this stream registry
   *
   * If the registry has already been started, the registered stream will also be
   * started.
   *
   * Before the registry has started, an error will be thrown if the stream name
   * has already been used.
   *
   * @param stream The stream to register
   */
  register(stream) {
    if (this.streams.has(stream.name)) {
      throw new Error(`Duplicate stream name: ${stream.name}`);
    }
    if (this.started) {
      stream.connect();
    }
    this.streams.set(stream.name, stream);
  }
  /**
   * Re-register a stream to this stream registry
   *
   * It will unsubscribe the previous stream with the same name and start the
   * new stream. It is only meant to be used internally for handling HMR.
   *
   * @param stream The stream to register
   */
  reRegister(stream) {
    var _a;
    (_a = this.streams.get(stream.name)) == null ? void 0 : _a.unsubscribe();
    if (this.started) {
      stream.connect();
    }
    this.streams.set(stream.name, stream);
  }
  /**
   * Start the registered reduced state streams.
   *
   * This causes the streams to actually start reducing over the action stream.
   *
   * Streams registered after this will also be started.
   *
   * If this is called twice without `stopReducing` being called in between, an
   * error will be thrown.
   *
   * @param action$ The action stream the streams should reduce over
   * @param states States to initialize the streams with, as returned by
   *               `getStates`
   */
  startReducing() {
    if (this.started) {
      throw new Error("Registry has already been started!");
    }
    this.started = true;
    for (const [, stream] of this.streams) {
      stream.connect();
    }
  }
  /**
   * Stops all the registered reduced state streams.
   *
   * This will call `unsubscribe` on all the streams. After this, all access to
   * the `.state` properties will throw exceptions. `getStates` can not be
   * called after `stopReducing` because of this.
   */
  stopReducing() {
    this.started = false;
    for (const stream of this.streams.values()) {
      stream.unsubscribe();
    }
  }
}
const stateStreamRegistry = new StateStreamRegistry();
const persistentDerivedStream = (name, source$, initialState) => {
  const stream = new ObservableState(name, source$, initialState);
  stateStreamRegistry.register(stream);
  return stream;
};
let HMR_IN_PROGRESS = false;
const isHmrInProgress = () => process.env.NODE_ENV === "development" && HMR_IN_PROGRESS;
const toPersistentStream = (name, source$, initialState) => {
  const stream = new ObservableState(name, source$, initialState);
  if (isHmrInProgress()) ;
  else {
    stateStreamRegistry.register(stream);
  }
  return stream;
};
const resolveInitialStateWhenHmrIsEnabled = (name, initialState) => {
  if (isHmrInProgress()) ;
  return initialState;
};
const persistentReducedStream = (name, initialState, reducers, options = {}) => {
  const resolvedInitialState = resolveInitialStateWhenHmrIsEnabled(
    name,
    initialState
  );
  const source$ = reducedStream$1(
    resolvedInitialState,
    reducers,
    options
  ).pipe(tag(name));
  return toPersistentStream(name, source$, resolvedInitialState);
};
const FLUSH_TIMEOUT = 64;
const MAX_DURATION = 1e3;
const FLUSH = "flush";
const allocateToBuffer = ({
  delay = FLUSH_TIMEOUT,
  maxDelay = MAX_DURATION
} = {}) => {
  const flush$ = new Subject();
  let timeout = 0;
  let expiresAt = 0;
  return pipe(
    tap$1(() => {
      window.clearTimeout(timeout);
      if (expiresAt !== 0 && performance.now() > expiresAt) {
        expiresAt = 0;
        timeout = 0;
        flush$.next(FLUSH);
        return;
      }
      timeout = window.setTimeout(() => {
        expiresAt = 0;
        flush$.next(FLUSH);
      }, delay);
      expiresAt = expiresAt === 0 ? performance.now() + maxDelay : expiresAt;
    }),
    bufferWhen(() => flush$)
  );
};
const toDispatchingPropsInternal = (prop, dispatch) => {
  if (isFunction(prop)) {
    return (...args) => dispatch(prop(...args));
  }
  if (isArray(prop)) {
    return prop.map(
      (p) => toDispatchingPropsInternal(p, dispatch)
    );
  }
  if (isObject(prop)) {
    return mapObject(
      prop,
      (p) => toDispatchingPropsInternal(p, dispatch)
    );
  }
  return prop;
};
const toDispatchingProps = (prop) => toDispatchingPropsInternal(prop, dispatchAction$1);
const isArray = (value) => Array.isArray(value);
const isFunction = (value) => typeof value === "function";
const isObject = (value) => {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};
const mapObject = (obj, fn) => {
  const result = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = fn(obj[key]);
    }
  }
  return result;
};
export {
  ObservableState,
  action$,
  actionCreator,
  actionReducer,
  allocateToBuffer,
  apply,
  carry,
  collectRoutines,
  combineReducers,
  connect,
  connectInstance,
  derivedStream,
  dispatchAction,
  extractPayload,
  isActionOfType,
  isValidRxBeachAction,
  mergeOperators,
  multiActionReducer,
  namespaceActionCreator,
  namespaceActionDispatcher,
  namespacedActionCreator,
  ofType,
  persistentDerivedStream,
  persistentReducedStream,
  reduceState,
  reducedStream,
  reducer,
  routine,
  spreadMap,
  stateStreamRegistry,
  streamReducer,
  subscribeRoutine,
  tapRoutine,
  toDispatchingProps,
  toPersistentStream,
  useStream,
  withNamespace,
  withoutNamespace
};
//# sourceMappingURL=index.js.map
